import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as automationApi from '../services/automationApi';
import { AutomationApproval } from '../types/Automation';
import { useAuth } from './AuthContext';

interface ApprovalNotificationContextType {
  pendingApprovals: AutomationApproval[];
  showApprovalModal: boolean;
  currentApproval: AutomationApproval | null;
  showModal: (approval: AutomationApproval) => void;
  hideModal: () => void;
  approveApproval: (approvalId: number) => Promise<void>;
  rejectApproval: (approvalId: number) => Promise<void>;
  refreshApprovals: () => Promise<void>;
  isLoading: boolean;
}

const ApprovalNotificationContext = createContext<ApprovalNotificationContextType | undefined>(undefined);

export const useApprovalNotification = () => {
  const context = useContext(ApprovalNotificationContext);
  if (!context) {
    throw new Error('useApprovalNotification must be used within an ApprovalNotificationProvider');
  }
  return context;
};

interface ApprovalNotificationProviderProps {
  children: React.ReactNode;
}

export const ApprovalNotificationProvider: React.FC<ApprovalNotificationProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [pendingApprovals, setPendingApprovals] = useState<AutomationApproval[]>([]);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [currentApproval, setCurrentApproval] = useState<AutomationApproval | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date>(new Date());

  // Initial check for pending approvals when user logs in
  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    // Initial check for pending approvals
    const checkInitialApprovals = async () => {
      try {
        const response = await automationApi.getAutomationApprovals({
          page: 1,
          limit: 10,
          status: 'pending'
        });

        if (response.approvals && response.approvals.length > 0) {
          setPendingApprovals(response.approvals);
          setLastChecked(new Date());
        }
      } catch (error) {
        console.error('Error checking initial approvals:', error);
      }
    };

    checkInitialApprovals();
  }, [isAuthenticated, user]);

  // Poll for new approval requests every 30 seconds (only when there are pending approvals)
  useEffect(() => {
    if (!isAuthenticated || !user || pendingApprovals.length === 0) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await automationApi.getAutomationApprovals({
          page: 1,
          limit: 10,
          status: 'pending'
        });

        if (response.approvals) {
          const newApprovals = response.approvals.filter((approval: AutomationApproval) => 
            new Date(approval.createdAt) > lastChecked
          );

          // If there are new approvals, show the first one
          if (newApprovals.length > 0) {
            setPendingApprovals(response.approvals);
            showModal(newApprovals[0]);
            setLastChecked(new Date());
          } else {
            setPendingApprovals(response.approvals);
          }
        }
      } catch (error) {
        console.error('Error polling for approvals:', error);
      }
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [lastChecked, isAuthenticated, user, pendingApprovals.length]);

  const showModal = useCallback((approval: AutomationApproval) => {
    setCurrentApproval(approval);
    setShowApprovalModal(true);
  }, []);

  const hideModal = useCallback(() => {
    setShowApprovalModal(false);
    setCurrentApproval(null);
  }, []);

  const approveApproval = useCallback(async (approvalId: number) => {
    try {
      setIsLoading(true);
      await automationApi.approveAutomation(approvalId);
      
      // Remove the approved approval from the list
      setPendingApprovals(prev => prev.filter(approval => approval.id !== approvalId));
      hideModal();
      
      // Show success message
      console.log('Automation approved successfully');
    } catch (error) {
      console.error('Error approving automation:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [hideModal]);

  const rejectApproval = useCallback(async (approvalId: number) => {
    try {
      setIsLoading(true);
      await automationApi.rejectAutomation(approvalId);
      
      // Remove the rejected approval from the list
      setPendingApprovals(prev => prev.filter(approval => approval.id !== approvalId));
      hideModal();
      
      // Show success message
      console.log('Automation rejected successfully');
    } catch (error) {
      console.error('Error rejecting automation:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [hideModal]);

  const refreshApprovals = useCallback(async () => {
    if (!isAuthenticated || !user) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await automationApi.getAutomationApprovals({
        page: 1,
        limit: 10,
        status: 'pending'
      });

      if (response.approvals) {
        setPendingApprovals(response.approvals);
      }
    } catch (error) {
      console.error('Error refreshing approvals:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  const value: ApprovalNotificationContextType = {
    pendingApprovals,
    showApprovalModal,
    currentApproval,
    showModal,
    hideModal,
    approveApproval,
    rejectApproval,
    refreshApprovals,
    isLoading
  };

  return (
    <ApprovalNotificationContext.Provider value={value}>
      {children}
    </ApprovalNotificationContext.Provider>
  );
};
